import { ThrottledFunction, throttle } from 'es-toolkit';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import XCircleIcon from '@flipster/react/icon/generated/XCircleIcon';
import { Portal } from '@flipster/react/misc/portal';
import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import segment, { ChartEvent } from '@flipster/utils/segment';
import { sendNativeRequest } from '@flipster/utils/simple-bridge/bridge';

import { IChartWidgetApi, ITimeScaleApi, ResolutionString } from '../../../lib/charting_library';
import { resolutionToSecs } from '../../../src/misc/helper';
import TradingView from '../../trading-view';
import { PointStats, TrianglePatternHelper, calculatePointStats } from './pattern-utils';
import { useChartPattern } from './use-chart-pattern';

const chartMarginBottom = 48;

interface Props {
  tv: TradingView;
  patternId: string;
}

interface VisibleRange {
  from: number;
  to: number;
}

// initialize -> setResolution -> updateFocusTime -> drawing

const PatternChartPlugin = ({ tv, patternId }: Props) => {
  const trianglePatternHelperRef = useRef<TrianglePatternHelper>(new TrianglePatternHelper(tv));
  const throttledFunction = useRef<ThrottledFunction<() => void>>(null);
  const [visiblePriceRange, setVisiblePriceRange] = useState<VisibleRange>();
  const [isDrawn, setIsDrawn] = useState(false);
  const [isIntervalUpdated, setIsIntervalUpdated] = useState(false);
  const { data, isExpired } = useChartPattern(patternId);
  const timer = useRef<NodeJS.Timeout>(null);

  const [labelX, setLabelX] = useState(0);
  const [labelY, setLabelY] = useState(0);

  const [chartWidth, setChartWidth] = useState(0);
  const [chartHeight, setChartHeight] = useState(0);

  const drawingData = useMemo(() => {
    if (!data || isExpired) return undefined;
    return data;
  }, [data, isExpired]);

  const pointStats = useMemo(() => {
    if (!drawingData?.drawing.payload.points) return undefined;
    return calculatePointStats(drawingData.drawing.payload.points);
  }, [drawingData]);

  const setPriceRange = (tv: TradingView, priceRange: { from: number; to: number }) => {
    const pane = tv.activeChart?.getPanes()[0];
    if (!pane) return;
    pane.getMainSourcePriceScale()?.setVisiblePriceRange(priceRange);
    setVisiblePriceRange(priceRange);
  };

  const updateFocusTime = useCallback(async () => {
    const chart = tv.activeChart;
    if (!drawingData || !pointStats || !chart) return;
    const { timeRange, priceRange } = getChartPatternVisibleRange(chart, pointStats, drawingData.intervalInMins);
    await chart.setVisibleRange(timeRange);
    setChartWidth(chart.getTimeScale().width() || 0);
    setChartHeight(chart.getPanes()[0].getHeight() || 0);
    setPriceRange(tv, priceRange);
  }, [drawingData, tv, pointStats]);

  useEffect(() => {
    // 차트의 터치 이벤트와 range 변경 이벤트를 구독한다
    const chart = tv.activeChart;
    if (!chart || !drawingData) return;

    const handleChangeVisibleRange = throttle(() => {
      const priceRange = getCurrentPriceRange(tv);
      if (priceRange) setPriceRange(tv, priceRange);
    }, 30);

    throttledFunction.current = handleChangeVisibleRange;

    const touchContainer = getTouchContainer();

    const registerEventHandlers = async () => {
      await tv.ready();

      // 터치 관성으로 인한 visibleRange 변경을 감지하기 위해 onVisibleRangeChanged 이벤트를 추가적으로 구독한다.
      chart.onVisibleRangeChanged().subscribe(null, handleChangeVisibleRange);
      touchContainer?.addEventListener('touchmove', handleChangeVisibleRange);
    };

    registerEventHandlers();

    return () => {
      chart.onVisibleRangeChanged().unsubscribe(null, handleChangeVisibleRange);
      touchContainer?.removeEventListener('touchmove', handleChangeVisibleRange);
      throttledFunction.current?.cancel();
    };
  }, [tv, tv.activeChart, drawingData]);

  useEffect(() => {
    const chart = tv.activeChart;
    if (!chart || !drawingData?.intervalInMins) return;
    const intervalUpdater = () => {
      trianglePatternHelperRef.current.clean();
      throttledFunction.current?.cancel();

      if (tv.interval === drawingData.intervalInMins) {
        // interval이 같은 경우에는, 바로 updated 상태를 변경한다.
        setIsIntervalUpdated(true);
      } else {
        // interval 변경시, interval이 변경된 이후에 visibleRange도 변경된다.
        // onIntervalChanged를 기점으로 변경되면, 차트가 다 준비되지 않은 상태이기 때문에, 이후 작업에서 에러가 발생한다.
        // 따라서 visibleRangeChanged 이벤트를 구독하여, 차트가 준비된 이후에 작업을 진행한다.
        chart.onVisibleRangeChanged().subscribe(null, () => setIsIntervalUpdated(true), true); // 마지막 params가 true면, 한 번만 실행되고 unsubscribe 된다.
        chart.setResolution(drawingData.intervalInMins as ResolutionString);
      }
    };

    intervalUpdater();
  }, [tv, tv.activeChart, drawingData]);

  useEffect(() => {
    const chart = tv.activeChart;
    if (!chart || !drawingData || !isIntervalUpdated) return;
    const trianglePatternHelper = trianglePatternHelperRef.current;

    // 간헐적으로 chart가 준비되지 않은 상황에서 visibleRange를 변경하면, visibleRange가 비정상적으로 변경되는 경우가 있다.
    // 이를 막기 위해서 chart가 준비될 때까지 의도적으로 약간의 딜레이를 준다.
    timer.current = setTimeout(() => {
      updateFocusTime();
    }, 100);

    chart.onVisibleRangeChanged().subscribe(
      null,
      async () => {
        const patternId = trianglePatternHelper.draw(drawingData.drawing.payload.points);
        if (patternId) setIsDrawn(true);
      },
      true,
    );

    return () => {
      if (timer.current) clearTimeout(timer.current);
      trianglePatternHelper.clean();
    };
  }, [tv, tv.activeChart, drawingData, isIntervalUpdated, updateFocusTime]);

  useEffect(() => {
    const chart = tv.activeChart;
    if (!chart || !drawingData || !isDrawn || !visiblePriceRange) return;

    setLabelX(calculateXPosition(tv, drawingData.drawing.payload.points[0].time));
    setLabelY(calculateYPosition(drawingData.drawing.payload.points[0].price, visiblePriceRange, chartHeight));
  }, [tv, drawingData, visiblePriceRange, chartHeight, isDrawn]);

  useEffect(() => {
    if (!isExpired) return;
    sendNativeRequest({
      name: 'showAlert',
      params: {
        title: 'Chart pattern expired',
        content: 'This pattern is no longer valid. We’ll notify you when a new one is detected.',
        positive_button: 'Got it',
        negative_button: null,
      },
      callback: 'onShowAlertResult',
    });
  }, [isExpired]);

  const isVisible = isDrawn && isWithinChartBounds(labelX, labelY, chartWidth, chartHeight);
  if (!isVisible || !pointStats) return null;

  return (
    <Portal selector='#tv-chart-container'>
      <div
        className={flex({
          pos: 'absolute',
          gap: 4,
          justifyContent: 'center',
          alignItems: 'center',
        })}
        style={{
          left: `${labelX}px`,
          bottom: `${labelY}px`,
        }}
      >
        <div className={css({ pos: 'relative' })}>
          <div className={css({ pos: 'absolute', top: '-25px', left: '30%', transform: 'translateX(-30%)' })}>
            <div className={flex()}>
              <div className={flex({ alignItems: 'center' })}>
                <button
                  className={flex({ color: 'content.default.level-1', alignItems: 'center' })}
                  onClick={() => {
                    if (!tv) return;
                    trianglePatternHelperRef.current.clean();
                    setIsDrawn(false);
                  }}
                >
                  <XCircleIcon size={16} />
                </button>
              </div>
              <div
                className={flex({
                  textStyle: 'Text/XS/Strong',
                  color: 'content.accent.info',
                  bgColor: 'background.accent.primary.tint',
                  py: 2,
                  px: 4,
                  borderRadius: 4,
                  gap: 4,
                  justifyContent: 'center',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                })}
                onClick={() => {
                  if (!data) return;
                  segment.track(ChartEvent.ChartIdentifierClick);
                  sendNativeRequest({
                    name: 'showChartPatternExplanationBottomSheet',
                    params: {
                      title: data.bottomSheetInfo.title,
                      url: data.bottomSheetInfo.imgUrl,
                      content: data.bottomSheetInfo.content,
                    },
                    callback: 'onShowChartPatternExplanationResult',
                  });
                }}
              >
                <div>{drawingData?.drawing.payload.label}</div>
                <ChevronRightIcon size={12} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default PatternChartPlugin;

const generatePositionToTimeFunction = (timeScale: ITimeScaleApi, x1: number, x2: number) => {
  if (x1 === x2) return () => 0;
  const time1 = timeScale.coordinateToTime(x1) || 0;
  const time2 = timeScale.coordinateToTime(x2) || 0;

  // tvChart의 coordinateToTime 함수는 price 값이 없는 좌표를 입력하면 null을 반환하여, 차트가 기록되지 이전의 위치를 계산할 수 없다.
  // 따라서 price 값이 유효한 time의 두 점을 이용해 일차 함수 방정식을 구하고, 이를 통해 좌표를 시간으로 변환한다.

  // f(x) = ax + b
  // time1 = ax1 + b
  // time2 = ax2 + b
  // b = time1 - ax1
  // time2 = ax2 + time1 - ax1
  // time2 - time1 = a(x2 - x1)

  // a = (time2 - time1) / (x2 - x1)
  // b = time1 - a * x1

  const a = (time2 - time1) / (x2 - x1);
  const b = time1 - a * x1;

  const positionToTime = (x: number) => a * x + b;
  return positionToTime;
};

const recoverPositionToTimeFunction = (timeScale: ITimeScaleApi) => {
  const width = timeScale.width();

  let leftPosition = 0;
  while (leftPosition < width) {
    if (timeScale.coordinateToTime(leftPosition)) break;
    leftPosition += width * 0.1;
  }

  let rightPosition = width;
  while (rightPosition > 0) {
    if (timeScale.coordinateToTime(rightPosition)) break;
    rightPosition -= width * 0.1;
  }

  return generatePositionToTimeFunction(timeScale, leftPosition, rightPosition);
};

const positionToTime = (timeScale: ITimeScaleApi, x: number) => {
  const time = timeScale.coordinateToTime(x);
  // x좌표가 차트를 넘어가는 경우, custom positionToTime 함수를 생성하여 시간으로 변환한다.
  if (time === null) {
    const recoveredFunction = recoverPositionToTimeFunction(timeScale);
    return recoveredFunction(x);
  }
  return time;
};

const calculateXPosition = (tv: TradingView, time: number): number => {
  const timeScale = tv.activeChart?.getTimeScale();
  if (!timeScale) return 0;

  const width = timeScale.width();

  const from = positionToTime(timeScale, 0);
  const to = positionToTime(timeScale, width);
  const distance = to - from;
  if (distance === 0) return 0;
  const x = ((time - from) / distance) * width;
  return x;
};

const calculateYPosition = (price: number, priceScale: VisibleRange, chartHeight: number): number => {
  const distance = priceScale.to - priceScale.from;
  const y = ((price - priceScale.from) / distance) * chartHeight + chartMarginBottom;
  return y;
};

const isWithinChartBounds = (x: number, y: number, width: number, height: number): boolean => {
  return x >= 0 && x <= width && y >= chartMarginBottom && y <= height;
};

const getTradingViewContainer = () => {
  const iframeSelector = '#tv-chart-container iframe';
  return (document.querySelector(iframeSelector) as HTMLIFrameElement)?.contentWindow?.document.body;
};

const getTouchContainer = () => {
  const touchContainerSelector = '.chart-markup-table';
  const tvContainer = getTradingViewContainer();
  const touchContainer = tvContainer?.querySelector(touchContainerSelector);
  return touchContainer ?? undefined;
};

const getCurrentPriceRange = (tv: TradingView) => {
  const chart = tv.activeChart;
  if (!chart) return { from: 0, to: 0 };
  try {
    const pane = chart.getPanes()[0];
    const mainPriceScale = pane.getMainSourcePriceScale();
    const priceRange = mainPriceScale?.getVisiblePriceRange();
    if (!priceRange) return { from: 0, to: 0 };
    return priceRange;
  } catch {
    return { from: 0, to: 0 };
  }
};

const getChartPatternVisibleRange = (chart: IChartWidgetApi, pointStats: PointStats, resolution: ResolutionString) => {
  const secs = resolutionToSecs(resolution);
  const candleCount = 10;
  const timeDistance = secs * candleCount;

  const newTimeRange = {
    from: chart.endOfPeriodToBarTime(pointStats.oldestTime - timeDistance),
    to: chart.endOfPeriodToBarTime(pointStats.newestTime + timeDistance),
  };

  const newPriceRange = {
    from: pointStats.lowestPrice * 0.99,
    to: pointStats.highestPrice * 1.02, // 상단에는 symbol label과 같은 공간이 있기 때문에 추가적으로 여유 공간을 준다.
  };

  return { timeRange: newTimeRange, priceRange: newPriceRange };
};
